import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  IconButton
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CancelIcon from '@mui/icons-material/Cancel';
import CalendarButton from "../../../components/common/CalendarButton";

const EventMobileCard = ({
  open,
  onClose,
  data,
  t,
  onSwapClick,
  onWithdrawClick,
}) => {
  const {
    titolo, luogo, start, end, ritrovo,
    mezzoName, autistaName, volontario1Name, volontario2Name, note,
    inizioTurno, fineTurno, isAutista, onRegistraOrario,
    myRole, hasActiveSwap,currentUserId
  } = data;
  const isMe = (id) => Number(id) === Number(data.currentUserId);
  const { canStart, canEnd } = useMemo(() => {
    const now = new Date();
    const inizio = inizioTurno ? new Date(inizioTurno) : null;
    return {
      canStart: !inizioTurno && now >= new Date(start),
      canEnd:   !!inizioTurno && !fineTurno && !!inizio && now >= new Date(inizio.getTime() + 30 * 60000),
    };
  }, [start, inizioTurno, fineTurno]);

  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(luogo)}`;

  const formatDate = d =>
    d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const formatTime = d =>
    d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });

  const calendarDescription = [
    `${t('Luogo')}: ${luogo}`,
    `${t('Orario di Ritrovo')}: ${formatTime(ritrovo)}`,
    `${t('Mezzo')}: ${mezzoName}`,
    `${t('Autista')}: ${autistaName}`,
    `${t('Volontario')}: ${volontario1Name}`,
    `${t('Volontario 2')}: ${volontario2Name}`,
  ].join('\n');

  const eventData = {
    title: titolo,
    start: start,
    end: end,
    description: calendarDescription,
    location: luogo,
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {titolo}
        <CalendarButton
          data={eventData}
          filename={`${titolo || 'evento'}.ics`}
          t={t}
          buttonProps={{
            sx: {
              color: "#2c5282",
              "&:hover": {
                backgroundColor: "#2c5282",
                color: "#fff",
              },
            },
          }}
        />
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ mb: 1 }}>
            {/* Stato operativo */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">{t('Stato Operativo')}</Typography>
              {!inizioTurno
                ? <Chip label={t('Pianificato')} size="small" sx={{ bgcolor: '#e0e0e0', color: '#555' }} />
                : !fineTurno
                  ? <Chip label={t('In Servizio')} size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
                  : <Chip label={t('Terminato')} size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
              }
            </Box>



            {/* Riepilogo orari (turno terminato) */}
            {fineTurno && (
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, textAlign: 'center', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('Iniziato alle')}: <strong>{new Date(inizioTurno).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('Terminato alle')}: <strong>{new Date(fineTurno).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</strong>
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                  {t('Per correzioni contattare il responsabile GAMES')}
                </Typography>
              </Box>
            )}

            {/* Bottoni Inizia/Termina (solo autista, non fineTurno) */}
            {isAutista && !fineTurno && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={!canStart}
                  onClick={() => onRegistraOrario('inizioTurno')}
                  sx={{
                    flex: 1,
                    bgcolor: inizioTurno ? '#2e7d32' : undefined,
                    '&:hover': { bgcolor: inizioTurno ? '#1b5e20' : undefined },
                    '&.Mui-disabled': {
                      bgcolor: inizioTurno ? '#2e7d32' : undefined,
                      color: inizioTurno ? '#fff' : undefined,
                      opacity: inizioTurno ? 0.85 : 0.4,
                    },
                  }}
                >
                  {inizioTurno
                    ? '▶ ' + new Date(inizioTurno).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : t('Inizia')}
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  disabled={!canEnd}
                  onClick={() => onRegistraOrario('fineTurno')}
                  sx={{
                    flex: 1,
                    bgcolor: fineTurno ? '#2e7d32' : '#e65100',
                    '&:hover': { bgcolor: fineTurno ? '#1b5e20' : '#bf360c' },
                    '&.Mui-disabled': {
                      bgcolor: fineTurno ? '#2e7d32' : undefined,
                      color: fineTurno ? '#fff' : undefined,
                      opacity: fineTurno ? 0.85 : 0.4,
                    },
                  }}
                >
                  {fineTurno
                    ? '⏹ ' + new Date(fineTurno).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : t('Termina')}
                </Button>
              </Box>
            )}
          </Box>

          <Divider />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography component="span">
              <strong>{t('Luogo')}:</strong> {luogo}
            </Typography>
            <IconButton
              component="a"
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              title={t('Apri in Maps')}
            >
              <LocationOnIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography>
            <strong>{t('Inizio')}:</strong> {formatDate(start)} {formatTime(start)}
          </Typography>
          <Typography>
            <strong>{t('Fine')}:</strong> {formatDate(end)} {formatTime(end)}
          </Typography>
          <Typography>
            <strong>{t('Orario di Ritrovo')}:</strong> {formatTime(ritrovo)}
          </Typography>

          <Divider />

          <Typography><strong>{t('Mezzo')}:</strong> {mezzoName}</Typography>
          <Typography><strong>{t('Squadra Appiedata')}:</strong> {data.squadraAppiedata === 1 ? 'Sì' : 'No'}</Typography>
          <Typography
              sx={isMe(data.autistaId) ? {
                bgcolor: '#ABEBC6',
                color: '#2c5282',
                fontWeight: 700,
                px: 1, py: 0.3,
                borderRadius: 1,
              } : { px: 1, py: 0.3 }}
            >
              <strong>{data.squadraAppiedata === 1 ? t('Volontario') : t('Autista')}:</strong> {autistaName}
          </Typography>
          <Typography
              sx={isMe(data.volontario1Id) ? {
                bgcolor: '#ABEBC6',
                color: '#2c5282',
                fontWeight: 700,
                px: 1, py: 0.3,
                borderRadius: 1,
              } : { px: 1, py: 0.3 }}
            >
              <strong>{t('Volontario')}:</strong> {volontario1Name}
            </Typography>
          <Typography
            sx={isMe(data.volontario2Id) ? {
              bgcolor: '#ABEBC6',
              color: '#2c5282',
              fontWeight: 700,
              px: 1, py: 0.3,
              borderRadius: 1,
            } : { px: 1, py: 0.3 }}
          >
            <strong>{t('Volontario 2')}:</strong> {volontario2Name}
          </Typography>

          <Divider />

          <Typography><strong>{t('Note')}:</strong> {note || '-'}</Typography>
          {myRole && start > new Date() && !fineTurno && (
            <Box sx={{ mt: 2 }}>
              {hasActiveSwap ? (
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  size="large"
                  startIcon={<CancelIcon />}
                  onClick={() => onWithdrawClick(data)}
                >
                  {t('Ritira Richiesta')}
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<SwapHorizIcon />}
                  onClick={() => onSwapClick(data)}
                >
                  {t('Cerca Sostituto')}
                </Button>
              )}
            </Box>
          )}  
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('Chiudi')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventMobileCard;